import React, { useContext } from 'react';
import MealType from 'models/MealType';
import MaterialIcon from 'react/elements/MaterialIcon';
import MealSlot from './MealSlot';
import { groceryListItemNames, groceryListLabel } from './groceryListLabel';
import { MealTipContext } from './MealChipTooltip';
import { eventTimeLabel } from './calendarEventTime';
import { mealsInSlot } from '../../../redux/food/dayEvents';

const NO_EVENTS = [];
const NO_LISTS = [];

/**
 * One day of the month grid: its number, the four meal slots, the Other row when it has
 * anything to hold, then the day's grocery lists and calendar events.
 *
 * The whole day is one target: tapping anywhere on it calls onDayClick, which opens the day's
 * meals in a popup. Its chips and pills are too small to aim at one by one on a phone. A meal can
 * still be dragged. The click that ends a drag is not stopped: react-beautiful-dnd only calls
 * preventDefault on it, and when the drag began and ended inside one day that click still reaches
 * the day. So a click already default-prevented is ignored rather than opening the popup.
 *
 * onDayClick gets the day's element as well as its date, for the popup to grow out of.
 *
 * In a month grid a hovered grocery list shows its items in the grid's tooltip; elsewhere they are
 * the pill's title.
 *
 * `dayEvent` is the day's meals and slot notes from the food store (selectDayEvents), or none for a
 * day with nothing on it; `calendarEvents` are the events starting on the day (selectCalendarEvents),
 * and `groceryLists` the lists bought on it (selectBoughtListsByDay).
 */
export default function MonthDayCell({
	date,
	dayEvent = null,
	calendarEvents = NO_EVENTS,
	groceryLists = NO_LISTS,
	isOutside = false,
	isToday = false,
	dragging = false,
	onDayClick,
}) {
	const tip = useContext(MealTipContext);
	const mealsIn = (type) => mealsInSlot(dayEvent, type);
	const otherMeals = mealsIn(MealType.types.OTHER);

	const classes = [
		'month-day',
		isOutside ? 'is-outside' : '',
		isToday ? 'is-today' : '',
		dragging ? 'is-dragging' : '',
	].filter(Boolean).join(' ');

	const open = (element) => onDayClick && onDayClick(date, element);
	const click = (event) => {
		if(!event.defaultPrevented) {
			open(event.currentTarget);
		}
	};
	// Only a key pressed on the day itself: a focused meal chip is a drag handle, and Space there
	// lifts the meal.
	const keyDown = (event) => {
		if(event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
			event.preventDefault();
			open(event.currentTarget);
		}
	};

	return (
		<div
			className={classes}
			data-date={date.format('YYYY-MM-DD')}
			role='button'
			tabIndex={0}
			aria-label={`${date.format('dddd, LL')}: show meals`}
			onClick={click}
			onKeyDown={keyDown}
		>
			<span className='month-day__number'>
				<span className='month-day__weekday'>{date.format('ddd')} </span>
				{date.date()}
			</span>
			<div className='month-day__slots'>
				{MealType.slots().map((slot) => (
					<MealSlot
						key={slot.id}
						date={date}
						slot={slot}
						meals={mealsIn(slot.id)}
					/>
				))}
				<MealSlot
					date={date}
					slot={MealType.other()}
					meals={otherMeals}
					collapsed={otherMeals.length === 0 && !dragging}
				/>
			</div>
			{(groceryLists.length > 0 || calendarEvents.length > 0) &&
			<div className='month-day__extras'>
				{groceryLists.map((groceryList) => (
					<span
						key={`list-${groceryList.key}`}
						className='month-day__pill month-day__pill--grocery-list'
						title={tip ? undefined : groceryListItemNames(groceryList)}
						onMouseEnter={tip ? (event) => tip.showList(groceryList, event.currentTarget) : undefined}
						onMouseLeave={tip ? tip.hide : undefined}
					>
						<MaterialIcon>shopping_cart</MaterialIcon>
						<span>{groceryListLabel(groceryList)}</span>
					</span>
				))}
				{calendarEvents.map((calendarEvent) => (
					<span
						key={`event-${calendarEvent.id}`}
						className='month-day__pill month-day__pill--event'
						title={`${eventTimeLabel(calendarEvent)}: ${calendarEvent.name}`}
					>
						{calendarEvent.name}
					</span>
				))}
			</div>
			}
		</div>
	);
}
